import { useCallback, useEffect, useState } from 'react';
import { PAGE_ITEMS_COUNT } from '../../../../contants/page';
import {
  CouponBulkHistoriesQuery,
  CouponBulkHistory,
  PreSignedGetUrlQuery,
  useCouponBulkHistoriesLazyQuery,
  useCouponPackMasterLazyQuery,
  usePreSignedGetUrlLazyQuery,
} from '../../../../graphql/generated';
import { downloadCsvFileWithBom } from '../../../../util/download';
import { formatJstDateTimeNumeric } from '../../../../util';

export type CouponPackBulkHistory = {
  id: string;
  publishDateTime: string;
  issuedNumber: number;
  description: string;
  bucket: string;
  key: string;
};

export type CouponPackBulkHistoriesQuery = {
  couponBulkHistories: {
    nodes: CouponPackBulkHistory[];
    pageInfo: {
      nextToken?: string;
    };
  };
};

export type UseCouponPackBulkHistories = {
  loading: boolean;
  downloadInProgressId: string;
  data?: CouponBulkHistoriesQuery;
  onFetchMore: () => void;
  onDownload: (row: CouponBulkHistory) => void;
  refetch: () => void;
};

/**
 * TODO: Replace with real GraphQL queries when API is ready
 * This is a mock implementation following the same pattern as useListCouponBulkHistories
 */
export const useCouponPackBulkHistories = (couponPackMasterId = ''): UseCouponPackBulkHistories => {
  const [downloadInProgressId, setDownloadInProgressId] = useState('');

  const [couponPackMasterQuery, { data, loading }] = useCouponPackMasterLazyQuery({
    fetchPolicy: 'network-only',
  });

  // クーポン一括発行履歴一覧を取得
  const [historiesQuery, queryContext] = useCouponBulkHistoriesLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: console.error,
  });

  // 再読み込み。新しくダウンロードを実行した後などを想定
  const refetch = useCallback(() => {
    historiesQuery({
      variables: {
        couponMasterId: couponPackMasterId || '',
        limit: PAGE_ITEMS_COUNT,
      },
    });
  }, [historiesQuery]);

  // クリックされた行の再ダウンロードURLを取得するLazy Query
  const [getBulkIssuedCouponsUrl] = usePreSignedGetUrlLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (query: PreSignedGetUrlQuery) => {
      downloadCsvFileWithBom(query.preSignedGetUrl.preSignedUrl, () => setDownloadInProgressId(''));
    },
  });

  // 行のダウンロードボタンを押したときにPreSignedUrlを取得して再ダウンロードする
  const onDownload = useCallback(
    (row: CouponBulkHistory): void => {
      if (!data?.couponPackMaster?.couponCode) {
        return;
      }
      setDownloadInProgressId(row.id);
      getBulkIssuedCouponsUrl({
        variables: {
          bucket: row.bucket,
          key: row.key,
          fileName: `${data.couponPackMaster.couponCode}_${formatJstDateTimeNumeric(row.publishDateTime)}.csv`,
        },
      });
    },
    [getBulkIssuedCouponsUrl, data?.couponPackMaster]
  );

  // さらに読み込むボタンクリック
  const onFetchMore = useCallback(async (): Promise<void> => {
    if (queryContext?.fetchMore && queryContext.variables) {
      await queryContext.fetchMore({
        variables: {
          couponMasterId: couponPackMasterId || '',
          nextToken: queryContext.data?.couponBulkHistories.pageInfo.nextToken,
          limit: PAGE_ITEMS_COUNT,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            couponBulkHistories: {
              __typename: fetchMoreResult.couponBulkHistories.__typename,
              nodes: [...prev.couponBulkHistories.nodes, ...fetchMoreResult.couponBulkHistories.nodes],
              pageInfo: fetchMoreResult.couponBulkHistories.pageInfo,
            },
          };
        },
      });
    }
  }, [queryContext]);

  useEffect(() => {
    couponPackMasterQuery({
      variables: {
        couponPackMasterId,
      },
    });
  }, [couponPackMasterQuery, couponPackMasterId]);

  useEffect(() => {
    refetch();
  }, [historiesQuery]);

  return {
    loading,
    downloadInProgressId,
    data: queryContext.data,
    onFetchMore,
    onDownload,
    refetch,
  };
};
